import React, { createRef, useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { TextBox, Button, ButtonLink } from "../..";
import PinInput from "react-pin-input";

import { ReactComponent as OtpEmailSvg } from "../../../assets/auth/signup/otp-mail.svg";
import { ReactComponent as CloseSvg } from "../../../assets/auth/login-close.svg";

// * Styles
import "../../../styles/otp.scss";
import useAuth from "../../../hooks/useAuth";
import SignUpContext from "./SignUpContext";
import { toast } from "react-toastify";
import AppContext from "../../../AppContext";
import { langComponent } from "../../../constants";

const OTP = ({ onContinue, ...props }) => {
  const [intervalId, setIntervalId] = useState(undefined);
  const [deadline, setDeadline] = useState(undefined);
  const [remainingTime, setRemainingTime] = useState("");
  const [toggleResend, setToggleResend] = useState(false);
  const [otp, setOtp] = useState("");
  const [isError, setIsError] = useState(false);
  const { confirmRegistration, resendOtp } = useAuth();
  const {
    credentials: { username },
    registrationId,
  } = useContext(SignUpContext);
  const { getLang } = useContext(AppContext);
  const Translation = getLang(langComponent.auth);
  const TranslationCommon = getLang(langComponent.common);

  const inputRef = createRef();

  useEffect(() => {
    setDeadline(props.deadline);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (!deadline) {
      return;
    }

    if (intervalId) {
      clearInterval(intervalId);
    }

    const id = setInterval(handleIntervalCallback, 1000);
    setIntervalId(id);
  }, [deadline]);

  const handleIntervalCallback = () => {
    const difference = deadline.getTime() - new Date().getTime();

    if (difference <= 0) {
      setToggleResend(true);
      return;
    }

    const minutes = Math.floor(difference / 60000);
    const seconds = Math.floor((difference / 1000) % 60);
    setRemainingTime(`${minutes}:${seconds.toString().padStart(2, "0")} min`);
  };

  /**
   * Resend OTP
   */
  const handleResendOTP = async () => {
    try {
      await resendOtp(registrationId);
      const newDeadline = new Date();
      newDeadline.setMilliseconds(
        newDeadline.getMilliseconds() + 5 * 60 * 1000
      );
      setDeadline(newDeadline);
      setToggleResend(false);
    } catch (error) {
      toast.error("Unable to resend OTP");
    }
  };

  const handleSubmit = (otp) => {
    confirmRegistration(registrationId, otp)
      .then(() => {
        onContinue();
      })
      .catch((error) => {
        setIsError(true);
        inputRef.current[0].clear();
      });
  };

  return (
    <div className="signup-otp">
      <OtpEmailSvg />
      <h2 className="signup-otp">{Translation("Check your email!")}</h2>
      <div className="signup-otp__desc">
        <span>{Translation("We have sent a code to")} </span>
        <span className="signup-otp__desc-email">{username}</span>
      </div>
      <div className="signup-otp-form">
        <PinInput
          ref={inputRef}
          length={6}
          initialValue={otp}
          type="numeric"
          inputMode="number"
          style={{ padding: "10px" }}
          inputStyle={{
            borderRadius: "6px",
            borderColor: !isError ? "#bebebe" : "#da3d34",
          }}
          inputFocusStyle={{ borderColor: "#0098ff" }}
          onChange={(value) => {
            setOtp(value);
          }}
          onComplete={(value) => {
            handleSubmit(value);
          }}
          autoSelect={true}
          regexCriteria={/^[ A-Za-z0-9_@./#&+-]*$/}
        />
        {isError && (
          <div className="signup-otp__error">
            {Translation("Incorrect code")}
          </div>
        )}
        {!toggleResend ? (
          <div className="signup-otp__remaining">
            {Translation("Remaining")} {remainingTime ? remainingTime : ""}
          </div>
        ) : (
          <ButtonLink
            content={Translation("Resend code")}
            onClick={handleResendOTP}
          />
        )}
        <Button
          type="submit"
          label={TranslationCommon("Confirm")}
          style={{
            margin: "20px 0 0",
          }}
          onClick={() => {
            handleSubmit(otp);
          }}
        />
      </div>
    </div>
  );
};

OTP.propTypes = {
  username: PropTypes.string,
  onContinue: PropTypes.func,
};

OTP.defaultProps = {
  username: "example@email.com",
  deadline: () => {
    const result = new Date();
    result.setMilliseconds(result.getMilliseconds() + 5 * 60 * 1000);
    return result;
  },
  onContinue: () => {},
};

export default OTP;
